// drafts/actions.js

import { v4 as uuidv4 } from "uuid";

import { Del } from "../../../models/Del/Del";
import { getDelInfo, createDelDraft } from "../../../api/DelApi";
import { getLocalDateString } from "../../../utils/dateUtils";
import { activeDelDraftsInitialState } from "./activeDelsDraftsState";
import { delFormInitialState } from "../../forms/delForm/delFormState";
// Importing the utility files for fetching del information
import { DelUtils } from "../utils/DelUtils";
// Importing the actions for the del form
import { makeDelFormActionsForTab } from "../../forms/delForm/delFormActions";

export const makeActiveDelDraftsActions = (set, get) => ({
  // lifecycle
  startNewDel: async (user) => {
    // Build local model immediately for UX, with locally generated IDs so the
    // draft is fully usable (autosave queueing included) even if the server
    // create below fails while offline.
    const delModel = new Del();
    delModel.updateDelId(uuidv4());
    // Keep the two JSA id fields aligned (blank models generate them separately)
    delModel.jsa.jsaBasicInfo.rowId = delModel.jsa.id;
    let createFailed = false;
    // Create on server under our client-generated IDs so offline patches match
    try {
      const { del_id, del_creation_date, jsa_id, waste_disposal_id } =
        await createDelDraft(user, 2, {
          delId: delModel.id,
          jsaId: delModel.jsa.id,
          wasteDisposalId: delModel.wasteDisposal.basicInfo.id,
        });
      console.log("Created new del draft with ids:", {
        del_id,
        jsa_id,
        waste_disposal_id,
      });
      // Update local model with server-provided IDs
      if (del_id) {
        delModel.updateDelId(del_id);
        delModel.del_creation_date = del_creation_date;
      } else {
        // Server responded but could not create; retry on reconnect
        createFailed = true;
      }
      // Update JSA and WasteDisposal IDs with server-provided values for database linking
      if (jsa_id) {
        delModel.jsa.id = jsa_id;
        delModel.jsa.jsaBasicInfo.rowId = jsa_id;
      }
      if (waste_disposal_id) {
        delModel.wasteDisposal.basicInfo.id = waste_disposal_id;
      }
      console.log("Updated del model after server creation:", delModel);
    } catch (e) {
      // Offline or request failed: keep the local IDs and register the draft
      // on the server when the connection returns (see ensureDraftCreated)
      createFailed = true;
    }
    set((s) => ({
      activeDelDrafts: {
        ...s.activeDelDrafts,
        currentDel: delModel,
        delMode: "new",
        delErrors: null,
        delActiveTab: "projects",
        delShowAll: false,
        pendingDraftCreates: createFailed
          ? [
              ...(s.activeDelDrafts.pendingDraftCreates || []).filter(
                (p) => p.delId !== delModel.id
              ),
              {
                delId: delModel.id,
                jsaId: delModel.jsa.id,
                wasteDisposalId: delModel.wasteDisposal.basicInfo.id,
                user: {
                  employee_id: user?.employee_id,
                  username: user?.username,
                },
              },
            ]
          : s.activeDelDrafts.pendingDraftCreates || [],
        rev: 0,
        delForm: {
          ...delFormInitialState,
          jsaFormUI: {
            ...delFormInitialState.jsaFormUI,
            base: {
              ...delFormInitialState.jsaFormUI.base,
              activeJsaTab: "All",
            },
          },
          wasteDisposalFormUI: {
            ...delFormInitialState.wasteDisposalFormUI,
            base: {
              ...delFormInitialState.wasteDisposalFormUI.base,
              activeWasteDisposalTab: "All",
            },
          },
        },
      },
    }));
    // Upsert minimal row into Drafts list
    try {
      const row = {
        id: delModel.id,
        del_creation_date: delModel.del_creation_date || getLocalDateString(),
        updated: delModel.del_creation_date || getLocalDateString(),
        del_date: delModel.projectInfo.date || "",
        client_name: delModel.projectInfo.clientName || "",
        project_number: delModel.projectInfo.projectNumber || "-",
        project_name_description: delModel.projectInfo.projectDescription || "",
        project_name: delModel.projectInfo.projectName || "",
        phase_id: delModel.projectInfo.phaseId || "",
        task_id: delModel.projectInfo.taskId || "",
        project_manager_name: delModel.projectInfo.projectManager?.name || "",
        invoice_manager_name: delModel.projectInfo.invoiceManager?.name || "",
      };
      get().delListsActions.upsertDraftLocal(row);
    } catch {}
  },

  // Register drafts that were created offline on the server, reusing their
  // locally generated IDs (idempotent server-side, safe on every reconnect).
  // Must succeed for a draft before its queued autosave patches are flushed,
  // otherwise they would target a DEL the server has never heard of.
  // Pass a delId to register just that draft, or nothing to register all.
  ensureDraftCreated: async (targetDelId = null) => {
    const list = get().activeDelDrafts.pendingDraftCreates || [];
    const targets = targetDelId
      ? list.filter((p) => String(p.delId) === String(targetDelId))
      : list;
    if (targets.length === 0) return { ok: true };
    let allOk = true;
    for (const entry of targets) {
      try {
        const { del_id, del_creation_date, jsa_id, waste_disposal_id } =
          await createDelDraft(entry.user, 2, {
            delId: entry.delId,
            jsaId: entry.jsaId,
            wasteDisposalId: entry.wasteDisposalId,
          });
        if (!del_id) {
          allOk = false;
          continue;
        }
        // Sync server-confirmed IDs into the open model if it is this draft
        const del = get().activeDelDrafts.currentDel;
        if (del && String(del.id) === String(entry.delId)) {
          if (!del.del_creation_date && del_creation_date) {
            del.del_creation_date = del_creation_date;
          }
          if (jsa_id && del.jsa) {
            del.jsa.id = jsa_id;
            if (del.jsa.jsaBasicInfo) del.jsa.jsaBasicInfo.rowId = jsa_id;
          }
          if (waste_disposal_id && del.wasteDisposal?.basicInfo) {
            del.wasteDisposal.basicInfo.id = waste_disposal_id;
          }
        }
        set((s) => ({
          activeDelDrafts: {
            ...s.activeDelDrafts,
            pendingDraftCreates: (
              s.activeDelDrafts.pendingDraftCreates || []
            ).filter((p) => String(p.delId) !== String(entry.delId)),
          },
        }));
      } catch (e) {
        console.warn(
          "Deferred draft creation failed, will retry on next reconnect:",
          e
        );
        allOk = false;
      }
    }
    return { ok: allOk };
  },

  fetchDel: async (delMode, delId) => {
    set((s) => ({
      activeDelDrafts: { ...s.activeDelDrafts, isLoading: true, error: null },
    }));
    // If this DEL is a draft created offline that the server does not know
    // about yet, register it first, and prefer the local model (its queued
    // patches have not been flushed to the server yet).
    const pendingEntry = (get().activeDelDrafts.pendingDraftCreates || []).find(
      (p) => String(p.delId) === String(delId)
    );
    if (pendingEntry) {
      try {
        await get().activeDelDraftsActions.ensureDraftCreated(delId);
      } catch {}
      const local = get().activeDelDrafts;
      if (local.currentDel?.id && String(local.currentDel.id) === String(delId)) {
        set((s) => ({
          activeDelDrafts: {
            ...s.activeDelDrafts,
            isLoading: false,
            error: null,
            delMode: "edit",
          },
        }));
        return { ok: true, local: true };
      }
    }
    try {
      const info = await getDelInfo(delId, delMode);
      if (info.del_info === false) {
        // The server does not know this DEL. If it is still pending offline
        // registration and we hold the local copy, keep it instead of
        // destroying the user's data.
        const local = get().activeDelDrafts;
        const stillPending = (local.pendingDraftCreates || []).some(
          (p) => String(p.delId) === String(delId)
        );
        if (
          stillPending &&
          local.currentDel?.id &&
          String(local.currentDel.id) === String(delId)
        ) {
          set((s) => ({
            activeDelDrafts: {
              ...s.activeDelDrafts,
              isLoading: false,
              error: null,
              delMode: "edit",
            },
          }));
          return { ok: true, local: true };
        }
        set((s) => ({
          activeDelDrafts: {
            ...s.activeDelDrafts,
            isLoading: false,
            error: "DEL not found or could not be loaded.",
            currentDel: null,
            delMode: null,
            rev: 0,
          },
        }));
        return { ok: false, error: "DEL not found or could not be loaded." };
      }
      const newDel = new Del(info.del_info);
      // Transform the del data
      // To prevent errors we need to make sure that the projects and employeePositions lists are loaded and keep checking if they are not
      // Wait for data to load with timeout safety
      const maxWait = 10000; // 10 seconds
      const startTime = Date.now();
      while (get().projects.loading || get().employeePositions.loading) {
        if (Date.now() - startTime > maxWait) {
          console.warn(
            "Timeout waiting for projects/employeePositions to load"
          );
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      const projects = get().projects.list;
      const employeePositions = get().employeePositions.list;
      const delUtils = new DelUtils(
        newDel,
        delMode,
        projects,
        employeePositions
      );
      const transformedDel = delUtils.transformDelData();
      const effectiveMode = delMode === "copy" ? "edit" : delMode;
      // Group available project-specific units by table name for the UI
      const rawPsu = info.del_info.available_project_specific_units || [];
      const groupedPsu = {};
      if (Array.isArray(rawPsu) && rawPsu.length > 0) {
        groupedPsu["all"] = rawPsu;
        rawPsu.forEach((unit) => {
          const tbl = unit.unit_table_name || "other";
          if (!groupedPsu[tbl]) groupedPsu[tbl] = [];
          groupedPsu[tbl].push(unit);
        });
      }
      set((s) => ({
        activeDelDrafts: {
          ...s.activeDelDrafts,
          isLoading: false,
          currentDel: transformedDel,
          delMode: effectiveMode,
          delActiveTab: "projects",
          delShowAll: false,
          rev: 0,
          delForm: {
            ...delFormInitialState,
            projectUI: {
              ...delFormInitialState.projectUI,
              // Hide JSA/Waste Disposal tabs based on not-required flags
              showJsaTab: !transformedDel.projectInfo.jsaNotRequired,
              showWasteDisposalTab:
                !transformedDel.projectInfo.wasteDisposalNotRequired,
              showClientSignaturesTab:
                !!transformedDel.projectInfo.clientSignaturesRequired,
            },
            unitsUI: {
              ...delFormInitialState.unitsUI,
              availableProjectUnits: groupedPsu,
            },
            jsaFormUI: {
              ...delFormInitialState.jsaFormUI,
              base: {
                ...delFormInitialState.jsaFormUI.base,
                activeJsaTab: "All",
              },
            },
            wasteDisposalFormUI: {
              ...delFormInitialState.wasteDisposalFormUI,
              base: {
                ...delFormInitialState.wasteDisposalFormUI.base,
                activeWasteDisposalTab: "All",
              },
            },
          },
        },
      }));
      // If this was a copy, ensure the new DEL appears in the Drafts list immediately
      if (delMode === "copy") {
        try {
          const row = {
            id: transformedDel.id,
            del_creation_date:
              transformedDel.del_creation_date || getLocalDateString(),
            updated: getLocalDateString(),
            del_date: transformedDel.projectInfo.date || "",
            client_name: transformedDel.projectInfo.clientName || "",
            project_number: transformedDel.projectInfo.projectNumber || "-",
            project_name_description:
              transformedDel.projectInfo.projectDescription || "",
            project_name: transformedDel.projectInfo.projectName || "",
            phase_id: transformedDel.projectInfo.phaseId || "",
            task_id: transformedDel.projectInfo.taskId || "",
            project_manager_name:
              transformedDel.projectInfo.projectManager?.name || "",
            invoice_manager_name:
              transformedDel.projectInfo.invoiceManager?.name || "",
          };
          get().delListsActions.upsertDraftLocal(row);
        } catch {}
      }
      return { ok: true };
    } catch (err) {
      // Network failure (e.g. offline reload). If the persisted local copy is
      // the same DEL, keep working from it rather than wiping the form.
      const local = get().activeDelDrafts;
      if (
        local.currentDel?.id &&
        String(local.currentDel.id) === String(delId)
      ) {
        set((s) => ({
          activeDelDrafts: {
            ...s.activeDelDrafts,
            isLoading: false,
            error: null,
            delMode: "edit",
          },
        }));
        return { ok: true, local: true };
      }
      set((s) => ({
        activeDelDrafts: {
          ...s.activeDelDrafts,
          isLoading: false,
          error: err,
          currentDel: null,
          delMode: null,
          rev: 0,
        },
      }));
      return { ok: false, error: err };
    }
  },

  // Hydrate the active draft DEL directly from a payload (e.g., copy/new endpoints)
  hydrateDelFromPayload: (delMode, delPayload) => {
    try {
      const newDel = new Del(delPayload);
      const projects = get().projects.list;
      const employeePositions = get().employeePositions.list;
      const delUtils = new DelUtils(
        newDel,
        delMode,
        projects,
        employeePositions
      );
      const transformedDel = delUtils.transformDelData();

      set((s) => ({
        activeDelDrafts: {
          ...s.activeDelDrafts,
          isLoading: false,
          currentDel: transformedDel,
          delMode: delMode === "copy" ? "edit" : delMode,
          delActiveTab: "projects",
          delShowAll: false,
          rev: 0,
          delForm: {
            ...delFormInitialState,
            projectUI: {
              ...delFormInitialState.projectUI,
              showJsaTab: !transformedDel.projectInfo.jsaNotRequired,
              showWasteDisposalTab:
                !transformedDel.projectInfo.wasteDisposalNotRequired,
              showClientSignaturesTab:
                !!transformedDel.projectInfo.clientSignaturesRequired,
            },
            jsaFormUI: {
              ...delFormInitialState.jsaFormUI,
              base: {
                ...delFormInitialState.jsaFormUI.base,
                activeJsaTab: "All",
              },
            },
            wasteDisposalFormUI: {
              ...delFormInitialState.wasteDisposalFormUI,
              base: {
                ...delFormInitialState.wasteDisposalFormUI.base,
                activeWasteDisposalTab: "All",
              },
            },
          },
        },
      }));

      return { ok: true, id: transformedDel.id };
    } catch (err) {
      set((s) => ({
        activeDelDrafts: {
          ...s.activeDelDrafts,
          isLoading: false,
          error: err,
        },
      }));
      return { ok: false, error: err };
    }
  },

  clearDel: () =>
    set((s) => ({
      activeDelDrafts: {
        ...activeDelDraftsInitialState,
        // keep tab identity
        tabName: "drafts",
        rev: 0,
        // keep offline drafts awaiting server registration
        pendingDraftCreates: s.activeDelDrafts.pendingDraftCreates || [],
      },
    })),

  // UI helpers
  setActiveTab: (tabName) =>
    set((s) => ({
      activeDelDrafts: { ...s.activeDelDrafts, delActiveTab: tabName },
    })),
  setShowAll: (show) =>
    set((s) => ({
      activeDelDrafts: { ...s.activeDelDrafts, delShowAll: show },
    })),
  setDelErrors: (errors) =>
    set((s) => ({
      activeDelDrafts: { ...s.activeDelDrafts, delErrors: errors },
    })),

  delFormActions: Object.freeze(
    makeDelFormActionsForTab(set, get, "activeDelDrafts")
  ),
});
